/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903558660 — CheckboxPage */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { ContentBox, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { debug } from '../../../../shared/lib/general';
import { CustomCheckbox } from '../../components/CustomCheckbox';
import { CustomTextField } from '../../components/CustomTextField';
import styles from '../../components/FeedbacksStyles.module.css';
import {
    COMPANY_IDS_FOR_FEEDBACKS,
    DISABLE_OR_ENABLE_BUTTON,
    FEEDBACKS_CUSTOMER_STATUS,
    FEEDBACK_ROUTES,
} from '../../feedback.constants';
import { UpdateFeedback } from '../../feedback.types';
import { useFeedbackStore } from '../../hooks/useFeedbackStore';
import { useUpdateFeedbacks } from '../../hooks/useUpdateFeedbacks';

export const CheckboxPage = () => {
    const vm = useAppStore((s) => s.vm);
    const feedback = useFeedbackStore((s) => s.feedback);
    const updatedFeedback = useFeedbackStore((s) => s.updateFeedback);
    const setUpdateFeedback = useFeedbackStore((s) => s.setUpdateFeedback);
    const setPageNumber = useFeedbackStore((s) => s.setPageNumber);
    const setNumberVisibility = useFeedbackStore((s) => s.setPageNumberVisible);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const questionsArray = feedback?.questions;
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>(
        {},
    );
    const [inputValue, setInputValue] = useState({
        comment: '',
    });
    const isNextButtonEnabled =
        Object.values(checkboxState).some((value) => value) ||
        inputValue.comment.trim() !== '' ||
        COMPANY_IDS_FOR_FEEDBACKS.includes(vm.companyId);
    const [hasError, setHasError] = useState<boolean>(false);
    const nextButtonStyles =
        isNextButtonEnabled && !hasError
            ? DISABLE_OR_ENABLE_BUTTON[0]
            : DISABLE_OR_ENABLE_BUTTON[1];

    const updateFeedbacksMutation = useUpdateFeedbacks();

    useEffect(() => {
        setPageNumber('2/3');
        setNumberVisibility(true);
        const initialCheckboxState: Record<string, boolean> = {};
        feedback.questions.forEach((question) => {
            const questionId = question.id;
            initialCheckboxState[questionId] = false;
        });
        setCheckboxState(initialCheckboxState);
    }, []);

    const handleCheckboxChange = (event: {
        target: { id: string; checked: boolean };
    }) => {
        const { checked } = event.target;
        const id = parseInt(event.target.id, 10);
        setCheckboxState((prevState) => ({
            ...prevState,
            [id]: checked,
        }));
        setSelectedIds((prevSelectedIds) => {
            if (checked) {
                if (!prevSelectedIds.includes(id)) {
                    return [...prevSelectedIds, id];
                }
            } else {
                return prevSelectedIds.filter(
                    (selectedId) => selectedId !== id,
                );
            }
            return prevSelectedIds;
        });
    };

    const handleInputChange = (event: {
        target: { id: string; value: string };
    }) => {
        setInputValue({
            comment:
                event.target.id === 'comment'
                    ? event.target.value
                    : inputValue.comment,
        });
    };

    const onContactPage = async () => {
        const newFeedbacks: UpdateFeedback = {
            ...updatedFeedback,
            chosen_options: selectedIds ? JSON.stringify(selectedIds) : null,
            custom_content: inputValue.comment ? inputValue.comment : null,
            customer_status: FEEDBACKS_CUSTOMER_STATUS.AFTER_WISHES_PAGE,
        };
        setUpdateFeedback(newFeedbacks);
        try {
            await updateFeedbacksMutation.mutateAsync({
                id: feedback.id,
                body: newFeedbacks,
            });
        } catch (e) {
            debug(`Checkbox submit failed: ${String(e)}`, 'error');
        }
        navigate(generatePath(FEEDBACK_ROUTES.contact, { id: vm.qr }));
    };

    const onCancelClick = () => {
        setNumberVisibility(false);
        navigate(generatePath(FEEDBACK_ROUTES.complete, { id: vm.qr }), {
            replace: true,
        });
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t("What didn't you like?")}</ScreenTitle>
            <ContentBox>
                <div className={styles.formGroupBox}>
                    {questionsArray.map((question) => {
                        return (
                            <CustomCheckbox
                                key={question.id}
                                id={String(question.id)}
                                onChange={handleCheckboxChange}
                                label={t(question.question)}
                            />
                        );
                    })}
                </div>

                <CustomTextField
                    id="comment"
                    label={t('Your comment')}
                    value={inputValue.comment}
                    onChange={handleInputChange}
                    rows={1.5}
                    setGlobalError={setHasError}
                />
                <div className={styles.feedbacksButtonBox}>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onCancelClick}
                    >
                        {t('Cancel')}
                    </PrimaryButton>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onContactPage}
                        disabled={!isNextButtonEnabled || hasError || updateFeedbacksMutation.isPending}
                        loading={updateFeedbacksMutation.isPending}
                        style={nextButtonStyles}
                    >
                        {t('Next')}
                    </PrimaryButton>
                </div>
            </ContentBox>
        </ScreenWrapper>
    );
};
